/**
 * Vehicle Service Records Slice — paginated-table model
 *
 * The page is now an org-wide paginated table (one row per service event) instead
 * of the previous per-vehicle drawer-tree. State accordingly:
 *
 *   list: {
 *     records, page, size, totalElements, totalPages,
 *     loading, error,
 *     // last-applied search inputs (echoed back so the page can show what was searched)
 *     appliedFilters: { rego, name, mobile } | null
 *   }
 *
 *   detailById: {
 *     [recordId]: { data, loading, error }
 *   }
 *
 *   recentByVehicle: {
 *     [vehicleUuid]: { records, loading, error }
 *   }
 *
 * The detail-by-id cache is preserved because expanding a row's drawer is still
 * a per-record fetch and we don't want to re-hit the server when the user
 * collapses and re-expands the same row within the same search session.
 *
 * The recentByVehicle cache backs the booking form's history preview — a tiny
 * (default 3) list of the most recent services for one vehicle, fetched without
 * a date range. Keyed by vehicleUuid so toggling the slide-out for the same
 * vehicle is free within a session.
 *
 * `clearAll` resets everything; `clearList` only clears the table without
 * dropping any expanded-drawer or recent-by-vehicle caches.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getVehicleServiceRecords,
    getVehicleServiceRecordById,
    getRecentServiceRecordsByVehicle,
} from '../../services/vehicleServiceRecordApi';

// ============================================
// Async Thunks
// ============================================

export const searchRecords = createAsyncThunk(
    'vehicleServiceRecords/searchRecords',
    async ({ rego, name, mobile, page = 0, size = 20 }, { rejectWithValue }) => {
        try {
            const response = await getVehicleServiceRecords({
                rego, name, mobile, page, size,
            });
            return {
                data: response.data,
                appliedFilters: { rego, name, mobile },
                requestedPage: page,
                requestedSize: size,
            };
        } catch (err) {
            return rejectWithValue({
                message: err.response?.data?.message
                    || 'Failed to load service records.',
            });
        }
    }
);

export const fetchRecordDetail = createAsyncThunk(
    'vehicleServiceRecords/fetchRecordDetail',
    async (id, { rejectWithValue }) => {
        try {
            const response = await getVehicleServiceRecordById(id);
            return { id, data: response.data };
        } catch (err) {
            return rejectWithValue({
                id,
                message: err.response?.data?.message
                    || 'Failed to load service history.',
            });
        }
    }
);

/**
 * Fetch the most recent N services for a single vehicle.
 * Backs the booking form's "View history" slide-out. Cached under the
 * vehicle UUID so toggling the panel for the same vehicle is free.
 */
export const fetchRecentByVehicle = createAsyncThunk(
    'vehicleServiceRecords/fetchRecentByVehicle',
    async ({ vehicleUuid, limit = 3 }, { rejectWithValue }) => {
        try {
            const response = await getRecentServiceRecordsByVehicle(vehicleUuid, limit);
            return { vehicleUuid, records: response.data ?? [] };
        } catch (err) {
            return rejectWithValue({
                vehicleUuid,
                message: err.response?.data?.message
                    || 'Failed to load recent services.',
            });
        }
    }
);

// ============================================
// Slice
// ============================================

const emptyList = {
    records:        [],
    page:           0,
    size:           20,
    totalElements:  0,
    totalPages:     0,
    loading:        false,
    error:          null,
    appliedFilters: null,
};

const initialState = {
    list:            { ...emptyList },
    detailById:      {},
    recentByVehicle: {},
};

const vehicleServiceRecordsSlice = createSlice({
    name: 'vehicleServiceRecords',
    initialState,
    reducers: {
        clearAll:  () => initialState,
        clearList: (state) => {
            state.list = { ...emptyList };
        },
        // Drop a single vehicle's cached recent list. Useful when the booking
        // form transitions a job to a terminal state and the history preview
        // should re-fetch next time it's opened.
        clearRecentByVehicle: (state, action) => {
            const uuid = action.payload;
            if (uuid && state.recentByVehicle[uuid]) {
                delete state.recentByVehicle[uuid];
            }
        },
    },
    extraReducers: (builder) => {
        builder
            // ── paginated search ─────────────────────────────────────────────
            .addCase(searchRecords.pending, (state, action) => {
                state.list = {
                    ...state.list,
                    loading: true,
                    error:   null,
                    page:    action.meta.arg.page ?? 0,
                    size:    action.meta.arg.size ?? state.list.size,
                };
            })
            .addCase(searchRecords.fulfilled, (state, action) => {
                const { data, appliedFilters, requestedPage, requestedSize } = action.payload;
                state.list = {
                    records:        data.content ?? [],
                    page:           data.number ?? requestedPage ?? 0,
                    size:           data.size ?? requestedSize ?? 20,
                    totalElements:  data.totalElements ?? 0,
                    totalPages:     data.totalPages ?? 0,
                    loading:        false,
                    error:          null,
                    appliedFilters,
                };
            })
            .addCase(searchRecords.rejected, (state, action) => {
                state.list = {
                    ...emptyList,
                    error: action.payload?.message || 'Failed to load.',
                };
            })

            // ── record detail by id ──────────────────────────────────────────
            .addCase(fetchRecordDetail.pending, (state, action) => {
                const id = action.meta.arg;
                state.detailById[id] = { data: null, loading: true, error: null };
            })
            .addCase(fetchRecordDetail.fulfilled, (state, action) => {
                const { id, data } = action.payload;
                state.detailById[id] = { data, loading: false, error: null };
            })
            .addCase(fetchRecordDetail.rejected, (state, action) => {
                const id = action.payload?.id ?? action.meta.arg;
                state.detailById[id] = {
                    data:    null,
                    loading: false,
                    error:   action.payload?.message || 'Failed to load.',
                };
            })

            // ── recent records for one vehicle (booking form preview) ────────
            .addCase(fetchRecentByVehicle.pending, (state, action) => {
                const uuid = action.meta.arg.vehicleUuid;
                state.recentByVehicle[uuid] = {
                    records: state.recentByVehicle[uuid]?.records ?? [],
                    loading: true,
                    loaded:  state.recentByVehicle[uuid]?.loaded ?? false,
                    error:   null,
                };
            })
            .addCase(fetchRecentByVehicle.fulfilled, (state, action) => {
                const { vehicleUuid, records } = action.payload;
                state.recentByVehicle[vehicleUuid] = {
                    records,
                    loading: false,
                    loaded:  true,
                    error:   null,
                };
            })
            .addCase(fetchRecentByVehicle.rejected, (state, action) => {
                const uuid = action.payload?.vehicleUuid ?? action.meta.arg.vehicleUuid;
                state.recentByVehicle[uuid] = {
                    records: [],
                    loading: false,
                    loaded:  true,
                    error:   action.payload?.message || 'Failed to load.',
                };
            });
    },
});

export const { clearAll, clearList, clearRecentByVehicle } = vehicleServiceRecordsSlice.actions;

// ============================================
// Selectors
// ============================================

export const selectList            = (state) => state.vehicleServiceRecords.list;
export const selectDetailById      = (state) => state.vehicleServiceRecords.detailById;
export const selectRecentByVehicle = (state) => state.vehicleServiceRecords.recentByVehicle;

/**
 * Selector factory: pick one vehicle's recent-services slot.
 * Returns a stable default shape when nothing has been fetched yet so the
 * consumer can destructure without nil-guards.
 */
/**
 * The empty slot, as ONE shared object.
 *
 * ⚠️ Returning a fresh `{ records: [], ... }` literal made every call a new reference, so
 * useSelector saw a change on every store update and React-Redux warned about it in the
 * console for any vehicle with no records loaded — which is every vehicle, until they
 * are. Frozen so a caller cannot mutate the value every other caller is sharing.
 */
const EMPTY_SLOT = Object.freeze({
    records: Object.freeze([]), loading: false, loaded: false, error: null,
});

export const selectRecentForVehicle = (vehicleUuid) => (state) =>
    state.vehicleServiceRecords.recentByVehicle[vehicleUuid] ?? EMPTY_SLOT;

export default vehicleServiceRecordsSlice.reducer;